import { PaymentTable } from "./PaymentTable"
import dayjs from "dayjs"
import { now, dateToServer } from "../../../utils/date"
import Button from "../../../components/Button/Button"
import { useDispatch } from "react-redux"
import {
  editNewLeadDetails,
  estimatePayments,
  getLeadDetails
} from "../../../features/contacts/contacts"
import React, { useEffect, useRef, useState } from "react"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { Frequency } from "../../../components/Frequency/Frequency"
import { StorageGetItem, USER } from "../../../lib/storage"
import { paymentScheduleEntryMode } from "./helper"

// The fee shape to send with an estimate, or nothing at all.
//
// Sent only when a flat fee is in play, so a percentage office's request is byte for byte what it
// always was. The server prices a flat plan from these (calculateProgramFeeAmount); without them a
// flat pick is estimated as a percentage of debt and the rep is shown the wrong monthly figure.
//
// A flat pick with no amount yet would price the plan at $0, so it sends nothing and lets the
// percentage path answer until the rep names a figure.
export const buildEstimateFeeShape = (programPlan) => {
  const amount = Number(programPlan?.flatFee)

  return programPlan?.feeType === 'flat' && amount > 0
    ? { feeType: 'flat', flatFee: amount }
    : {}
}


// The request the Calculate button sends.
//
// `userId` is the part that was missing. The server resolves the contact's OFFICE from it, and
// without an office resolveRequestedFeeShape refuses a flat pick as "not set up to charge a flat
// fee" and prices the plan as a percentage of enrolled debt instead — silently, with the wrong
// figure shown to the rep and printed on what the client signs. It is also what
// shouldDisableInitialFee reads, so that office setting was inert here too.
//
// Extracted from getPaymentSchedule so the payload can be asserted directly; the shape it builds
// is unchanged apart from the id.
export const buildEstimatePayload = ({ programPlan, paymentSchedule, frequency, userId }) => {
  const freq = programPlan?.frequency || frequency

  const programLength = (() => {
    const plan = programPlan?.plan

    switch (freq) {
      case "semiMonthly":
        return plan * 2
      case "custom":
        return plan * (paymentSchedule?.paymentDays?.length || 1)
      case "weekly":
      case "every2":
      case "monthly":
      default:
        return plan
    }
  })()

  return {
    amount: programPlan?.amount,
    programLength,
    custodialFee: programPlan?.custodialFee,
    programFee: programPlan?.programFee,
    ...buildEstimateFeeShape(programPlan),
    initialFee: programPlan?.initialFee,
    frequency: freq,
    paymentDays: paymentSchedule?.paymentDays,
    startFromMonth: paymentSchedule?.startFromMonth,
    startDate: dateToServer(paymentSchedule?.cycleStartDate),
    ...(userId ? { userId } : {}),
    ...(freq === "semiMonthly" ? { secondStartDate: dateToServer(paymentSchedule?.secondDate) } : {}),
  }
}

export const PaymentSchedule = ({
  programPlan,
  paymentSchedule,
  setPaymentSchedule,
  setSelectedTab,
  userId,
  isCreate,
  getNextWorkingDay,
  setDisableTabs,
  recalcRequestedRef,
}) => {

  const { triggerLoading } = useMyContextLoading()
  const dispatch = useDispatch()
  const currentURL = window.location.href
  const parts = currentURL.split('/')
  const userIdByUrl = parts[parts.length - 1]

  const [reqError, setReqError] = useState('')
  const lastCalcSignature = useRef('')
  const recomputeArmedRef = useRef(false)

  const frequency = programPlan?.frequency || paymentSchedule?.frequency || 'monthly'
  const isSimpleFrequency = ['weekly', 'every2', 'monthly'].includes(frequency)

  const buildCalcSignature = () => JSON.stringify({
    amount: programPlan?.amount,
    plan: programPlan?.plan,
    custodialFee: programPlan?.custodialFee,
    programFee: programPlan?.programFee,
    // In the signature so switching shape, or changing the flat amount, re-estimates. Without it
    // the rep would keep seeing the schedule for the shape they just moved away from.
    feeType: programPlan?.feeType,
    flatFee: programPlan?.flatFee,
    initialFee: programPlan?.initialFee,
    frequency,
    startDate: paymentSchedule?.cycleStartDate?.toISOString?.() || paymentSchedule?.cycleStartDate || null,
    secondDate: paymentSchedule?.secondDate?.toISOString?.() || paymentSchedule?.secondDate || null,
    paymentDays: paymentSchedule?.paymentDays || [],
    startFromMonth: paymentSchedule?.startFromMonth || null,
  })

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    const mode = paymentScheduleEntryMode({ recalcRequested: !!recalcRequestedRef?.current, isCreate })
    if (mode === 'recompute') {
      recalcRequestedRef.current = false
      recomputeArmedRef.current = true
      getPaymentSchedule()
    } else if (mode === 'load-saved') {
      recomputeArmedRef.current = false
      getData()
    } else {
      recomputeArmedRef.current = true
    }
  }, [])

  useEffect(() => {
    if (!recomputeArmedRef.current) return
    const customIncomplete = frequency === 'custom' &&
      (!paymentSchedule?.paymentDays || paymentSchedule?.paymentDays?.length === 0 || !paymentSchedule?.startFromMonth)
    const semiMonthlyIncomplete = frequency === 'semiMonthly' && !isValidDate(paymentSchedule?.secondDate)
    const ready = programPlan?.amount && programPlan?.plan && isValidDate(paymentSchedule?.cycleStartDate) && !customIncomplete && !semiMonthlyIncomplete
    if (!ready) return
    const sig = buildCalcSignature()
    if (sig === lastCalcSignature.current) return
    lastCalcSignature.current = sig
    getPaymentSchedule()
  }, [programPlan?.amount, programPlan?.plan, programPlan?.custodialFee, programPlan?.programFee, programPlan?.initialFee, paymentSchedule?.cycleStartDate, paymentSchedule?.secondDate, paymentSchedule?.paymentDays, paymentSchedule?.startFromMonth, frequency])

  const isValidDate = (date) => {
    return dayjs.isDayjs(date) ? date.isValid() : false
  }

  const getPaymentSchedule = async () => {
    lastCalcSignature.current = buildCalcSignature()
    triggerLoading(true)
    const data = buildEstimatePayload({
      programPlan,
      paymentSchedule,
      frequency,
      // The contact's own id. `custUserId` is set the moment step one creates the contact, so it
      // is right on both routes; the URL segment is the edit route's source and is the enrolment
      // path's literal "add-contact" when there is no contact yet, so it is never used there.
      userId: userId || (isCreate ? '' : userIdByUrl),
    })
    const response = await dispatch(estimatePayments(data))
    if (response.error) {
      setReqError(response.payload)
      lastCalcSignature.current = ''
    } else {
      setPaymentSchedule((prev) => ({ ...prev, tableData: response?.payload?.data, frequency: programPlan?.frequency || prev.frequency || 'monthly' }))
    }
    triggerLoading(false)
  }

  const getData = async () => {
    triggerLoading(true)
    try {
      const response = await dispatch(getLeadDetails(userIdByUrl || userId))

      const paymentScheduleData = response?.payload?.data?.paymentSchedule || {}

      const cycleStartDateFromDb = paymentScheduleData.startDate
        ? dayjs.utc(paymentScheduleData.startDate)
        : null

      const secondDateFromDb = paymentScheduleData.secondStartDate
        ? dayjs.utc(paymentScheduleData.secondStartDate)
        : null

      const paymentDaysFromDb = [...(paymentScheduleData.paymentDays || [])].sort((a, b) => a - b)
      const startFromMonthFromDb = paymentScheduleData.startFromMonth || null

      setPaymentSchedule(prev => ({
        ...prev,
        frequency: programPlan?.frequency || prev.frequency || paymentScheduleData.frequency || "monthly",
        cycleStartDate: isValidDate(prev.cycleStartDate) ? prev.cycleStartDate : (cycleStartDateFromDb || dayjs()),
        secondDate: isValidDate(prev.secondDate)
          ? prev.secondDate
          : (secondDateFromDb || (
              (programPlan?.frequency || prev.frequency || paymentScheduleData.frequency) === 'semiMonthly' && cycleStartDateFromDb
                ? cycleStartDateFromDb.add(15, 'day')
                : null
            )),
        tableData: paymentScheduleData.estimatedPayments || prev.tableData || [],
        paymentDays: prev.paymentDays?.length ? prev.paymentDays : paymentDaysFromDb,
        startFromMonth: prev.startFromMonth || startFromMonthFromDb,
      }))
    } catch (error) {
      setReqError(error.message || 'Failed to get customer data')
    }
    triggerLoading(false)
  }


  const updatePaymentSchedule = async () => {
    const paymentDays = [...(paymentSchedule.paymentDays || [])].sort((a, b) => a - b)
    const postData = {
      paymentSchedule: {
        frequency: programPlan?.frequency || frequency,
        startDate: dateToServer(paymentSchedule?.cycleStartDate),
        ...((programPlan?.frequency || frequency) === "semiMonthly" ? { secondStartDate: dateToServer(paymentSchedule?.secondDate) } : {}),
        estimatedPayments: paymentSchedule?.tableData,
        paymentDays,
        startFromMonth: paymentSchedule.startFromMonth,
      }
    }
    try {
      const response = await dispatch(editNewLeadDetails({ userId, data: postData }))
      if (response.error) {
        setReqError(response.payload)
      } else {
        showSnackBar("success", "Contact successfully updated")
      }
    } catch (error) {
      setReqError('An unexpected error occurred. Please try again.')
    }
  }
  const customCondition = paymentSchedule.frequency === 'custom' &&
    (!paymentSchedule?.paymentDays || paymentSchedule?.paymentDays?.length === 0 || !paymentSchedule?.startFromMonth)

  const [userRole, setUserRole] = useState('')
  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  return (
    <>
      {!isSimpleFrequency && (
        <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px] mb-6">
          <div className="font-bold text-xl-20 pt-[24px] pl-[48px] pb-[16px]">
            Payment Schedule
          </div>
          <div className="border-b border-divider mx-6" />
          <div className="mx-12 mb-12">
            <Frequency
              paymentSchedule={{ ...paymentSchedule, frequency }}
              setPaymentSchedule={setPaymentSchedule}
              frequencyReadOnly={true}
              showFrequencyDropdown={false}
              hideStartDate={true}
              userRole={userRole}
            />
          </div>
        </div>
      )}
      <div className="mt-6 mb-6 flex justify-end space-x-4">
        <Button
          width="min-w-[156px]"
          onClick={() => {
            recomputeArmedRef.current = true
            getPaymentSchedule()
          }}
        >
          Calculate
        </Button>
        <Button
          disabled={
            paymentSchedule.frequency === '' ||
            paymentSchedule?.tableData?.length === 0 ||
            customCondition ||
            !isValidDate(paymentSchedule?.cycleStartDate)
          }
          width="min-w-[156px]"
          onClick={() => {
            setDisableTabs((prev) => ({ ...prev, review: false }))
            updatePaymentSchedule()
            setSelectedTab("review")
          }}
        >
          Save
        </Button>
      </div>
      <div className="bg-surface border border-divider shadow-lg rounded-2xl w-full max-w-[798px]">
        <div className="font-bold text-xl-20 pt-6 px-12 pb-4 flex justify-between items-center">
          Estimated Payments {`(${paymentSchedule?.tableData?.length})`}
        </div>

        <div className="border-b border-divider mx-6" />
        <div className="mx-12 my-6">
          <PaymentTable
            data={paymentSchedule?.tableData}
          />
        </div>
      </div>
    </>
  )
}